import { IntlShape, MessageDescriptor } from 'react-intl';
import { ASSET_SPECS } from 'fym-shared/src/music24/assetSpecs';
import { CatalogItem, KitAssetStage } from '../../../../redux/api/fymAPI.types';
import { releaseThemeMessages } from '../themes/releaseThemeMessages';
import { assetStyleMessages } from '../themes/assetStyleMessages';
import { kitVariantLabel } from '../kitLabels';
import { studioThemeMessages } from '../studio/studioThemeMessages';
import { isAiBudgetFailure, isMasterFailure, kitFailText } from '../studio/kitFailMessage';
import { etaShortText, renderPartText, waitsForText } from '../studio/fullVideoProgressText';
import { reelFailMessages } from '../studio/aiReel/reelProgressMessages';
import { reelFailAction } from '../studio/aiReel/reelStages';
import { WorkRow } from './studioWorkModel';
import { studioWorkMessages } from './studioWorkMessages';

const STAGE_MESSAGES: Record<KitAssetStage, MessageDescriptor> = {
  queued: studioWorkMessages.stageQueued,
  song: studioWorkMessages.stageSong,
  plan: studioWorkMessages.stagePlan,
  render: studioWorkMessages.stageRender,
};

/**
 * What the failed row offers: the same item again, the catalog (master), the song form (reel), the
 * upgrade (the Free AI budget is spent), or nothing.
 */
export type WorkFailAction = 'tryAgain' | 'openCatalog' | 'changeSong' | 'upgrade' | 'none';

export interface WorkRowText {
  /** "Countdown · 3 days before", "AI reel · 30 s". */
  name: string;
  /** "Dreamy · Cover card". */
  meta: string;
  /** "Rendering", "Ready", "Could not make it". */
  step: string;
  /** A line under the bar: the video queue rule, or "Waits for your lyric video" (design s10). */
  hint: string | null;
  /** A full video that works: "about 9 min left" (design s10); else null. */
  eta: string | null;
  /** The reason of a failure, in plain words (the fail copy of e3, m2b, n5 and of the reel page). */
  why: string | null;
  failAction: WorkFailAction;
  /** "Credit back" or "No credit used". */
  refund: string | null;
}

function isVideo(row: WorkRow): boolean {
  return row.kind === 'reel' || ASSET_SPECS[row.asset.specId]?.kind === 'video';
}

/** The words of one row of the strip (design r2). Pure. */
export function workRowText(
  intl: IntlShape,
  row: WorkRow,
  /** The song of the row; undefined for a song that the catalog no longer holds. */
  item: Pick<CatalogItem, 'song'> | undefined
): WorkRowText {
  // A full video that renders says its part: "Rendering part 4 of 8" (design s10).
  const partText = row.kind === 'asset' && row.state === 'working' ? renderPartText(intl, row.asset) : null;
  const step =
    row.state === 'failed'
      ? intl.formatMessage(studioWorkMessages.failed)
      : row.state === 'ready'
        ? intl.formatMessage(row.kind === 'reel' ? studioWorkMessages.readyToReview : studioWorkMessages.ready)
        : (partText ?? intl.formatMessage(STAGE_MESSAGES[row.stage ?? 'queued']));
  const waitsFor = row.state === 'working' && row.waitsFor !== null ? waitsForText(intl, row.waitsFor) : null;
  const hint =
    waitsFor ??
    (row.state === 'working' && row.stage === 'queued' && isVideo(row)
      ? intl.formatMessage(studioWorkMessages.queueHint)
      : null);
  const eta = row.state === 'working' ? etaShortText(intl, row.etaSec) : null;

  if (row.kind === 'reel') {
    const { reel } = row;
    const action = row.state === 'failed' ? reelFailAction(reel.failReason) : null;
    return {
      name: intl.formatMessage(studioWorkMessages.reelName, { seconds: reel.durationSec }),
      meta: intl.formatMessage(releaseThemeMessages[reel.themeId].name),
      step,
      hint,
      eta,
      why: row.state === 'failed' ? intl.formatMessage(reelFailMessages[reel.failReason ?? 'unknown']) : null,
      failAction: action === null ? 'none' : action === 'backToStudio' ? 'none' : action,
      refund: row.state === 'failed' ? intl.formatMessage(studioWorkMessages.creditBack) : null,
    };
  }

  const { asset } = row;
  const typeName = intl.formatMessage(studioThemeMessages[asset.themeId].name);
  const variant = kitVariantLabel(intl, asset);
  const masterFailReason = item?.song?.masterFailReason;
  const isMaster = row.state === 'failed' && isMasterFailure(asset.failCode, masterFailReason);
  return {
    // A type with one plain video ("Lyric video") needs no "· Video".
    name: variant === typeName || asset.variant === 'video' ? typeName : `${typeName} · ${variant}`,
    meta: [
      intl.formatMessage(releaseThemeMessages[asset.releaseThemeId].name),
      asset.style === null ? null : intl.formatMessage(assetStyleMessages[asset.style].name),
    ]
      .filter((part): part is string => part !== null)
      .join(' · '),
    step,
    hint,
    eta,
    // The "Credit back" chip says the refund, so the reason has no refund line here.
    why: row.state === 'failed' ? kitFailText(intl, asset, masterFailReason, { refund: false }) : null,
    failAction: isMaster ? 'openCatalog' : isAiBudgetFailure(asset.failCode) ? 'upgrade' : 'tryAgain',
    refund:
      row.state === 'failed'
        ? intl.formatMessage(asset.credits > 0 ? studioWorkMessages.creditBack : studioWorkMessages.noCredit)
        : null,
  };
}
